import { useState } from "react";
import { ghostBtnClass, mfgPrimaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { useAuth } from "@/contexts/AuthContext";
import {
  continueBuilderAssistantSession,
  submitBuilderAssistantSession,
} from "@/lib/manufacturing/builderAssistantClient";
import type { BuilderAssistantSummary } from "@/lib/manufacturing/builderAssistantTypes";
import { canMarkReadyForFinalCert, readFinalCertHistory, readFinalQc, readReadyForFinalCert } from "@/lib/manufacturing/finalQc";
import { markReadyForFinalCert } from "@/lib/manufacturing/qcDashboardClient";

export function BuilderAssistantFinish({
  sessionId,
  summary,
  sessionSummary,
  completed,
  onBeforeSubmit,
  onSubmitted,
  onContinued,
}: {
  sessionId: string;
  summary: BuilderAssistantSummary;
  sessionSummary: Record<string, unknown>;
  completed: boolean;
  onBeforeSubmit?: () => Promise<{ ok: true } | { ok: false; error: string }>;
  onSubmitted: () => void;
  onContinued: () => void;
}) {
  const auth = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const summaryRecord = sessionSummary;
  const ready = readReadyForFinalCert(summaryRecord);
  const passed = readFinalQc(summaryRecord);
  const earlier = readFinalCertHistory(summaryRecord);
  const canMark = canMarkReadyForFinalCert(auth.roles);

  async function submit() {
    setBusy(true);
    setError(null);
    if (onBeforeSubmit) {
      const flushed = await onBeforeSubmit();
      if (!flushed.ok) {
        setBusy(false);
        setError(flushed.error);
        return;
      }
    }
    const res = await submitBuilderAssistantSession(sessionId);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    onSubmitted();
  }

  async function continueEditing() {
    setBusy(true);
    setError(null);
    const res = await continueBuilderAssistantSession(sessionId);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    onContinued();
  }

  return (
    <section className="space-y-2 rounded-lg border border-gray-200 p-4 dark:border-zinc-700">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Inspection summary</h3>
      <ul className="text-sm text-gray-700 dark:text-zinc-300">
        <li>{summary.observation_count} observations</li>
        <li>{summary.qc_findings} QC findings</li>
        <li>{summary.build_book_candidates} build-book candidates</li>
        <li>{summary.general} general observations</li>
        <li>{summary.missing_evidence} awaiting a photo</li>
      </ul>
      <p className="text-xs text-gray-500 dark:text-zinc-400">
        Submit a section when that step is finished, or leave sections open and submit the inspection once. Submit inspection saves every section and closes the audit. It does not move the build pipeline or release labor pay.
      </p>
      {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
      {earlier.length > 0 ? (
        <p className="text-xs text-gray-600 dark:text-zinc-300">
          Ready for final certification was recorded for {earlier.map((mark) => mark.by_label || "QC").join(", ")}.
        </p>
      ) : null}
      {ready ? (
        <p className="text-sm text-gray-800 dark:text-zinc-100">
          Ready for final certification{ready.by_label ? ` · ${ready.by_label}` : ""}.
        </p>
      ) : null}
      {completed && canMark && !passed && !ready ? (
        <button
          type="button"
          className={mfgPrimaryBtnClass}
          disabled={busy}
          onClick={() => {
            setBusy(true);
            setError(null);
            void markReadyForFinalCert(sessionId).then(async (res) => {
              setBusy(false);
              if (!res.ok) {
                setError(res.error);
                return;
              }
              onContinued();
            });
          }}
        >
          Ready for final certification
        </button>
      ) : null}
      {completed ? (
        <button type="button" className={ghostBtnClass} disabled={busy} onClick={() => void continueEditing()}>
          Continue editing
        </button>
      ) : (
        <button type="button" className={mfgPrimaryBtnClass} disabled={busy} onClick={() => void submit()}>
          Submit inspection
        </button>
      )}
    </section>
  );
}
